<template>
  <div class="invoices">
    <panel title="Dokumenty księgowe">
      <div class="document-info">
        <p>
          ID Wniosku:

          <strong>
            {{ documentIndex.documentId }}
          </strong>
        </p>

        <p>
          Uczeń:
          <strong>
            {{ studentFullName }}
          </strong>
        </p>

        <p>
          Wnioskodawca:
          <strong>
            {{ applicantFullName }}
          </strong>
        </p>
      </div>

      <decision-data :document-index="documentIndex" />

      <decision-benefit-form-details :decision="decision" />

      <w-table
        :add-button-entity-type="addButtonEntityType"
        :add-button-entity-id="documentId"
        :entity-type="entityType"
        :columns="columns"
        :data="list"
        :actions="actions"
        :total="0"
        :per-page="-1"
        :foot-data="footData"
        @add="showAddModal"
        @edit="showEditModal($event.id)"
        @delete="showDeleteDialog($event.id)"
        @approve="approve($event.id)"
        @unlock="unlock($event.id)"
      />

      <template slot="footer">
        <w-button class="btn-secondary" text="Wróć" @click="goBack" />
      </template>
    </panel>

    <invoice-modal
      :id="idToEdit"
      :show="showModal"
      :max-to-pay="maxToPay"
      :invoices="list"
      @save="add"
      @close="hideModal"
      @update="update"
    />
  </div>
</template>

<script>
import { getPath } from '@witkac/utils/getPath';
import { setPath } from '@witkac/utils/setPath';
import { mapState } from 'vuex';

import DecisionData from '@/components/document/DecisionData';
import DecisionBenefitFormDetails from '@/components/forms/invoice/DecisionBenefitFormDetails';
import InvoiceModal from '@/components/modals/InvoiceModal';
import Panel from '@/components/panel/Panel';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { InvoiceStatus } from '@/enums/invoiceStatus.enum';
import { Successes } from '@/enums/successes.enum';
import { TableName } from '@/enums/tableName';
import { date, dateTime } from '@/filters/date';
import { listMixin } from '@/mixins/list';
import {
  APP_AMOUNT_LOCALE,
  APPROVE_INVOICE_ACTION,
  DELETE_INVOICE_ACTION,
  EDIT_INVOICE_ACTION,
  UNLOCK_INVOICE_ACTION
} from '@/utils/constants';
import { getInvoicesList } from '@/utils/invoices';
import { tryParseInt } from '@/utils/number';
import { formatAmount } from '@/utils/tables';

const messages = {
  listError: Errors.INVOICE_CANNOT_LIST,
  getError: Errors.INVOICE_CANNOT_GET,
  added: Successes.INVOICE_ADDED,
  addError: Errors.INVOICE_CANNOT_ADD,
  updated: Successes.INVOICE_UPDATED,
  updateError: Errors.INVOICE_CANNOT_UPDATE,
  deleteConfirm: Successes.INVOICE_CONFIRM_DELETE,
  deleted: Successes.INVOICE_DELETED,
  deleteError: Errors.INVOICE_CANNOT_DELETE,
  approved: Successes.INVOICE_APPROVED,
  unlocked: Successes.INVOICE_UNLOCKED
};

// @vuese
// Lista faktur
// @group Document
export default {
  components: { DecisionBenefitFormDetails, DecisionData, InvoiceModal, Panel, WTable },
  mixins: [listMixin('invoice', messages)],
  data() {
    return {
      actions: [APPROVE_INVOICE_ACTION, UNLOCK_INVOICE_ACTION, EDIT_INVOICE_ACTION, DELETE_INVOICE_ACTION],
      columns: [
        { name: 'number', displayName: 'Numer', isSumRow: true, calcSumRow: false, sortable: true },
        {
          cssClass: 'text-right',
          displayName: 'Kwota',
          isSumRow: true,
          name: 'price',
          nameTransformer: formatAmount,
          sortable: true
        },
        {
          cssClass: 'text-right',
          displayName: 'Kwota podlegająca refundacji',
          isSumRow: true,
          name: 'priceToPay',
          nameTransformer: formatAmount,
          sortable: true
        },
        {
          cssClass: 'text-right',
          displayName: 'Pozostała należna kwota stypendium',
          name: 'index',
          nameTransformer: index => {
            const totalPaid = this.list
              .filter((_, invoiceIndex) => invoiceIndex < index)
              .map(invoice => invoice.priceToPay * 100)
              .reduce((total, priceToPay) => total + priceToPay, 0);
            const leftToPay = (this.totalToPay * 100 - totalPaid) / 100;
            if (leftToPay >= 0) {
              return leftToPay.toLocaleString(APP_AMOUNT_LOCALE, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }
            return (
              'Przekroczono o ' +
              (-leftToPay).toLocaleString(APP_AMOUNT_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            );
          }
        },
        { name: 'nip', displayName: 'NIP', sortable: true },
        { name: 'date', displayName: 'Data wystawienia', nameTransformer: date, sortable: true },
        { name: 'created', displayName: 'Data wprowadzenia', nameTransformer: dateTime, sortable: true },
        { name: 'mdokUid', displayName: 'UID dokumentu MDOK' },
        {
          name: 'status',
          displayName: 'Status faktury',
          cssClass: 'text-center',
          nameTransformer: value => (value === InvoiceStatus.APPROVED ? 'Z' : 'N')
        },
        { name: 'description', displayName: 'Uwagi' }
      ],
      entityType: 'invoice',
      addButtonEntityType: 'document'
    };
  },
  computed: {
    ...mapState({
      decision: state => state.documentIndex.data.currentDecision,
      documentIndex: state => state.documentIndex.data
    }),

    totalToPay() {
      return this.documentIndex.decisionSecondYearTotal;
    },

    studentFullName() {
      return this.documentIndex?.data?.tenderer?.fullName || 'Brak uzupełnienia';
    },

    applicantFullName() {
      return this.documentIndex?.data?.applicant?.fullName || 'Brak uzupełnienia';
    },

    entityId() {
      return this.$route.params.documentId.toString();
    },

    maxToPay() {
      const alreadyPaid = this.list
        .filter(item => item.id !== this.idToEdit)
        .map(item => item.priceToPay)
        .reduce((total, invoiceToPay) => total + invoiceToPay, 0);

      return this.totalToPay - alreadyPaid;
    },

    documentId() {
      return tryParseInt(this.$route.params.documentId);
    },

    footData() {
      const footData = {};
      this.columns.forEach(column => {
        if (column.isSumRow && column.calcSumRow !== false) {
          const value = this.list.reduce((all, element) => {
            const val = getPath(element, column.name);
            if (!!val) {
              return all + parseFloat(val);
            }
            return all;
          }, 0);
          setPath(footData, value, column.name);
        }
      });
      footData.number = 'Razem:';
      return footData;
    }
  },
  methods: {
    // @vuese
    // Wraca do dokumentu
    goBack() {
      this.$router.push({ name: 'documentDetails' });
    },

    // @vuese
    // Dodaje dane encji do zapisywanego modelu
    prepareModel(data) {
      data.entityType = TableName.document;
      data.entityId = this.entityId;
      return data;
    },

    // @vuese
    // Przeładowuje listę po dodaniu faktury
    afterAdd() {
      this.updateList(this.$route, undefined, () => void 0);
    },

    // @vuese
    // Przeładowuje listę po aktualizacji faktury
    afterUpdate() {
      this.updateList(this.$route, undefined, () => void 0);
    },

    // @vuese
    // Aktualizuje listę
    updateList(to, from, next) {
      return getInvoicesList(to)
        .then(() => next && next())
        .catch(err => messages.listError);
    },

    // @vuese
    // Akceptacja faktury
    approve(id) {
      return this.$store
        .dispatch('invoice/approve', { id })
        .then(this.notifySuccess(messages.approved))
        .then(() => this.updateList(this.$route, undefined, () => void 0));
    },

    // @vueuse
    // Odblokowanie faktury do edycji
    unlock(id) {
      return this.$store
        .dispatch('invoice/unlock', { id })
        .then(this.notifySuccess(messages.unlocked))
        .then(() => this.updateList(this.$route, undefined, () => void 0));
    }
  }
};
</script>

<style>
.invoices strong {
  font-weight: bold;
}
</style>
